// Tela do Faturamento na Visão Geral (#6500): Alice e Ítalo. Abre no dia, sem seletor.
//
// Pergunta da tela: o que eu calculo e emito agora, e estou no prazo?
//   - fila do dia: o status de cada fatura (Faturas e Cobranças);
//   - perto do limite e cobertura: a Conferência do Faturamento;
//   - faturas para revisar: puladas com injeção e motivo que não vale para a origem
//     da injeção (regra da trava do pulo). Ter injeção não quer dizer que foi de usina
//     da Vigor: cada fatura mostra de onde veio;
//   - minha taxa e minha produção: as emissões do Dashboard de Faturamento (1ª emissão
//     de cada fatura), da própria pessoa.
//   - erro e retrabalho (#6509): boletos cancelados por erro na emissão ÷ emitidos pelo time,
//     pelo motivo do cancelamento, que virou lista fechada.
import React, { useMemo } from 'react';
import { CartaoErroERetrabalho, useErroERetrabalho } from '../blocosFase3';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ClipboardCheck, FileInput, FileText, PieChart } from 'lucide-react';
import { mesAtualFaturas } from '../../../src/lib/conferenciaFaturamentoDb';
import { mesAnterior, resumirConferencia } from '../../../src/lib/conferenciaFaturamento';
import { formatarKwh } from '../../../src/lib/pularCalculoInjecao';
import {
  META_ATE_1_DIA_UTIL, META_COBERTURA, META_FORA_SEM_JUSTIFICATIVA, coberturaDoFaturamento, minhasEmissoes,
} from '../../../src/lib/visaoGeral/faturamento';
import { BlocosDoDia } from '../BlocosDoDia';
import { BotaoAcao, CabecalhoVisaoGeral, CartaoHoje, CartaoIndicador, ListaAtalhos, PainelDetalhe, SecaoVisaoGeral, plural } from '../componentes';
import { useConferenciaDoMes } from '../useGestaoFinanceiro';
import { useFaturasParaRevisar, useFilaDoFaturamento, useProducaoDoMes } from '../useFaturamentoDoDia';
import type { PropsTelaVisaoGeral } from '../tipos';

const pct = (v: number, casas = 1) => `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: casas })}%`;
const dataCurta = (iso: string | null) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—');
const LISTA = 8;

const TelaFaturamento: React.FC<PropsTelaVisaoGeral> = ({ pessoa, souEu, definicao, periodo }) => {
  const navigate = useNavigate();
  const { inicioMes, fimMes } = useMemo(() => {
    const h = new Date();
    return { inicioMes: new Date(h.getFullYear(), h.getMonth(), 1), fimMes: new Date(h.getFullYear(), h.getMonth() + 1, 1) };
  }, []);
  const mesAtual = mesAtualFaturas();
  const mesPassado = mesAnterior(mesAtual) ?? mesAtual;
  const mesesRevisar = useMemo(() => [mesAtual, mesPassado], [mesAtual, mesPassado]);

  // ── dados ──
  const { fila, erro: erroFila } = useFilaDoFaturamento();
  const conferencia = useConferenciaDoMes(mesAtual);
  const conferenciaPassada = useConferenciaDoMes(mesPassado);
  const revisar = useFaturasParaRevisar(mesesRevisar);
  const producao = useProducaoDoMes(inicioMes, pessoa.full_name ?? null);

  const resumo = useMemo(() => (conferencia.carga ? resumirConferencia(conferencia.carga.resultado) : null), [conferencia.carga]);
  const resumoPassado = useMemo(() => (conferenciaPassada.carga ? resumirConferencia(conferenciaPassada.carga.resultado) : null), [conferenciaPassada.carga]);
  const pertoDoLimite = useMemo(() => (conferencia.carga
    ? conferencia.carga.resultado.linhas
      .filter(l => l.estadoPrazo === 'limite_proximo' || l.estadoPrazo === 'limite_vencido')
      .sort((a, b) => (a.diasPrazo ?? 0) - (b.diasPrazo ?? 0))
    : null), [conferencia.carga]);

  const minhas = useMemo(() => (producao.emissoes ? minhasEmissoes(producao.emissoes, pessoa.full_name ?? null, inicioMes, fimMes) : null), [producao.emissoes, pessoa.full_name, inicioMes, fimMes]);
  const doTime = useMemo(() => (producao.emissoes ? minhasEmissoes(producao.emissoes, null, inicioMes, fimMes) : null), [producao.emissoes, inicioMes, fimMes]);
  const taxa1Dia = minhas && minhas.comChegada ? minhas.ate1DiaUtil / minhas.comChegada : null;
  const taxaFora = minhas && minhas.total ? minhas.foraSemJustificativa / minhas.total : null;
  const cobertura = resumoPassado ? coberturaDoFaturamento(resumoPassado) : null;
  const coberturaAgora = resumo ? coberturaDoFaturamento(resumo) : null;
  const revisarVigor = revisar.lista?.filter(r => r.origem === 'vigor').length ?? 0;
  const revisarTerceiro = (revisar.lista?.length ?? 0) - revisarVigor;
  const pulosNoMes = producao.pulosPorDia ? [...producao.pulosPorDia.values()].reduce((t, n) => t + n, 0) : null;
  const erro = useErroERetrabalho(inicioMes, fimMes, doTime ? doTime.total : null);

  // Dia a dia do mês até hoje: boletos emitidos e faturas puladas pela pessoa.
  const diaADia = useMemo(() => {
    if (!minhas || !producao.pulosPorDia) return null;
    const hoje = new Date();
    return Array.from({ length: hoje.getDate() }, (_, i) => {
      const d = new Date(hoje.getFullYear(), hoje.getMonth(), i + 1);
      const chave = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return { dia: String(i + 1), emitidos: minhas.porDia.get(chave) ?? 0, pulados: producao.pulosPorDia!.get(chave) ?? 0 };
    });
  }, [minhas, producao.pulosPorDia]);

  const acoes = souEu ? <BotaoAcao rotulo="Faturas e Cobranças" icone={FileText} para="/financeiro/faturas" /> : null;
  const abrirUc = (uc: string | null) => navigate(uc ? `/financeiro/faturas?busca=${encodeURIComponent(uc)}` : '/financeiro/faturas');

  return (
    <div className="space-y-8">
      <CabecalhoVisaoGeral nome={pessoa.full_name} pergunta={definicao.pergunta} periodo={periodo} acoes={acoes} />

      <BlocosDoDia pessoa={pessoa} perfil="faturamento" />

      <SecaoVisaoGeral titulo="Hoje" subtitulo="a fila do faturamento agora">
        <CartaoHoje
          valor={fila ? fila.paraValidar + fila.paraCalcular : null}
          rotulo="faturas para calcular"
          tom="atencao"
          para="/financeiro/faturas"
          dica={fila ? `${fila.paraValidar} esperando a validação e ${fila.paraCalcular} validadas esperando o cálculo. Parcelas de acordo, UCs da cobrança agrupada e puladas ficam de fora.` : undefined}
        />
        <CartaoHoje
          valor={fila ? fila.paraEmitir : null}
          rotulo="boletos para emitir"
          tom="atencao"
          para="/financeiro/faturas"
          dica="Faturas calculadas esperando o boleto (sem as parcelas de acordo e as UCs da cobrança agrupada)."
        />
        <CartaoHoje
          valor={resumo ? resumo.limiteProximo + resumo.limiteVencido : null}
          rotulo="perto ou além do limite de emissão"
          tom={resumo && resumo.limiteVencido > 0 ? 'critico' : 'atencao'}
          para="/financeiro/conferencia-faturamento"
          dica={resumo ? `${resumo.limiteVencido} já passaram do limite e ${resumo.limiteProximo} vencem em até 2 dias (limite = vencimento da Equatorial menos 7 dias corridos, ou 5 úteis).` : undefined}
        />
        <CartaoHoje
          valor={revisar.lista ? revisarVigor : null}
          rotulo="puladas com injeção de usina da Vigor"
          tom="critico"
          dica={revisar.lista
            ? `Puladas em ${mesPassado} e ${mesAtual} com injeção de usina da Vigor e sem um motivo que justifique não cobrar: boleto que deixou de sair. Mais ${plural(revisarTerceiro, 'pulada', 'puladas')} com injeção de usina de terceiro e motivo a corrigir.`
            : undefined}
        />
      </SecaoVisaoGeral>
      {(erroFila || conferencia.erro) && <p className="-mt-6 text-xs text-red-600 dark:text-red-400">{erroFila || conferencia.erro}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PainelDetalhe titulo="Faturas para revisar">
          {revisar.lista === null ? (
            <div className="h-24 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
          ) : revisar.semUsinas ? (
            <p className="text-sm text-amber-700 dark:text-amber-300">Não foi possível ler a lista de usinas da Vigor, então não dá para separar a origem da injeção agora.</p>
          ) : revisar.lista.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma fatura pulada com injeção e motivo errado em {mesPassado} e {mesAtual}.</p>
          ) : (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                <strong className={revisarVigor ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}>{revisarVigor}</strong> com usina da Vigor ·{' '}
                <strong className="text-gray-900 dark:text-white">{revisarTerceiro}</strong> com usina de terceiro e motivo a corrigir
              </p>
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {revisar.lista.slice(0, LISTA).map(r => (
                  <li key={r.fatura.id}>
                    <button type="button" onClick={() => abrirUc(r.fatura.uc_codigo)}
                      className="w-full flex items-start gap-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg px-2 -mx-2">
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{r.fatura.cliente_nome || 'Sem nome'}</span>
                        <span className="block text-xs text-gray-400">UC {r.fatura.uc_codigo} · {r.fatura.mes_referencia} · pulada como "{r.motivo}"{r.fatura.pular_calculo_por_nome ? ` por ${r.fatura.pular_calculo_por_nome}` : ''}</span>
                      </span>
                      <span className={`shrink-0 text-right text-xs font-semibold ${r.origem === 'vigor' ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>
                        {r.origem === 'vigor' ? `Usina da Vigor${r.classificacao.usina_vigor ? `: ${r.classificacao.usina_vigor}` : ''}` : r.classificacao.kwh_nao_identificado > 0 && r.classificacao.kwh_outras === 0 ? 'Usina não identificada' : 'Usina de terceiro'}
                        <span className="block font-normal text-gray-500 dark:text-gray-400">{formatarKwh(r.origem === 'vigor' ? r.classificacao.kwh_vigor : r.classificacao.kwh_total)} kWh injetados</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-400 mt-3">
                Ter injeção não quer dizer que foi de usina da Vigor. Vermelho: usina da Vigor, a conta precisa de boleto ou de um motivo que justifique não cobrar.
                Amarelo: usina de terceiro, o motivo certo é "Injeção só de usina que não é da Vigor", com o número da usina lido no PDF.
                {revisar.lista.length > LISTA && ` Mais ${plural(revisar.lista.length - LISTA, 'fatura', 'faturas')}.`}
              </p>
            </>
          )}
        </PainelDetalhe>

        <PainelDetalhe titulo="Perto do limite de emissão" acao={{ rotulo: 'Conferência', para: '/financeiro/conferencia-faturamento' }}>
          {pertoDoLimite === null ? (
            <div className="h-24 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
          ) : pertoDoLimite.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma fatura de {mesAtual} a 2 dias ou menos do limite.</p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {pertoDoLimite.slice(0, LISTA).map(l => (
                <li key={l.chave}>
                  <button type="button" onClick={() => abrirUc(l.uc)}
                    className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg px-2 -mx-2">
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{l.cliente}</span>
                      <span className="block text-xs text-gray-400">UC {l.uc} · vence na Equatorial em {dataCurta(l.dataVencimento)}</span>
                    </span>
                    <span className={`text-xs whitespace-nowrap font-semibold ${l.estadoPrazo === 'limite_vencido' ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>
                      {l.estadoPrazo === 'limite_vencido' ? `limite passou há ${plural(Math.abs(l.diasPrazo ?? 0), 'dia', 'dias')}` : l.diasPrazo === 0 ? 'limite é hoje' : `limite em ${plural(l.diasPrazo ?? 0, 'dia', 'dias')}`}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </PainelDetalhe>
      </div>

      <SecaoVisaoGeral titulo="Meu mês" subtitulo={`${mesAtual}, emissões feitas por ${(pessoa.full_name || '').split(' ')[0] || 'você'}`}>
        <CartaoIndicador
          titulo="Emitidas em até 1 dia útil"
          valor={minhas ? (taxa1Dia == null ? '—' : pct(taxa1Dia)) : null}
          meta={taxa1Dia != null ? { texto: `meta ${pct(META_ATE_1_DIA_UTIL, 0)}`, atingido: taxa1Dia / META_ATE_1_DIA_UTIL } : undefined}
          detalhe={minhas ? `${minhas.ate1DiaUtil} de ${minhas.comChegada} boletos saíram até 1 dia útil depois de a fatura chegar.${doTime && doTime.comChegada ? ` Time: ${pct(doTime.ate1DiaUtil / doTime.comChegada)}.` : ''}` : undefined}
          origem="Só a 1ª emissão de cada fatura, sem boleto avulso (mesma conta do Dashboard de Faturamento). Chegada = a fatura entrar no sistema."
        />
        <CartaoIndicador
          titulo="Fora do prazo sem justificativa"
          valor={minhas ? (taxaFora == null ? '—' : pct(taxaFora)) : null}
          meta={taxaFora != null ? { texto: `meta: até ${pct(META_FORA_SEM_JUSTIFICATIVA)}`, atingido: taxaFora <= 0 ? 1 : META_FORA_SEM_JUSTIFICATIVA / taxaFora } : undefined}
          detalhe={minhas ? `${plural(minhas.foraSemJustificativa, 'boleto', 'boletos')} depois do limite sem motivo; ${minhas.foraDoPrazo} fora do prazo no total.` : undefined}
          para="/financeiro"
          origem="Limite = vencimento da Equatorial menos 7 dias corridos (5 úteis). Atrasos de mais de 45 dias (carga retroativa) ficam de fora, como no Dashboard de Faturamento."
        />
        <CartaoIndicador
          titulo={`Cobertura de ${mesPassado}`}
          valor={cobertura == null ? (resumoPassado ? '—' : null) : pct(cobertura)}
          meta={cobertura != null ? { texto: `meta ${pct(META_COBERTURA, 0)}`, atingido: cobertura / META_COBERTURA } : undefined}
          detalhe={resumoPassado ? `${resumoPassado.faturado + resumoPassado.puladoJustificado} de ${resumoPassado.base} UCs do rateio faturadas ou puladas com motivo.${coberturaAgora != null ? ` ${mesAtual} até agora: ${pct(coberturaAgora)}.` : ''}` : undefined}
          para="/financeiro/conferencia-faturamento"
          origem="Conferência do Faturamento. É do time: a UC sem fatura recebida também conta como não coberta."
        />
        <CartaoErroERetrabalho e={erro} rotuloMes={mesAtual} titulo="Erro e retrabalho do time" />
      </SecaoVisaoGeral>

      <section className="space-y-3" aria-label="Detalhe">
        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Detalhe</h2>
        <PainelDetalhe titulo="Minha produção dia a dia">
          {diaADia === null ? (
            <div className="h-56 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
          ) : (
            <div className="h-56 -ml-2">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={diaADia} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="text-gray-200 dark:text-gray-700" />
                  <XAxis dataKey="dia" tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={false} interval={1} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={false} width={32} />
                  <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} labelFormatter={(d) => `Dia ${d}`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v: string) => <span className="text-gray-600 dark:text-gray-300">{v}</span>} />
                  <Bar dataKey="emitidos" name="Boletos emitidos" stackId="p" fill="#5F6C37" isAnimationActive={false} />
                  <Bar dataKey="pulados" name="Faturas puladas" stackId="p" fill="#C9D3A8" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          <p className="text-xs text-gray-400 mt-2">
            {minhas && pulosNoMes != null ? `${plural(minhas.total, 'boleto emitido', 'boletos emitidos')} e ${plural(pulosNoMes, 'fatura pulada', 'faturas puladas')} em ${mesAtual}. ` : ''}
            Faturas calculadas e validadas por pessoa ficam no registro de ações, que só a gestão lê (aba Equipes do Painel Executivo).
          </p>
        </PainelDetalhe>
      </section>

      <ListaAtalhos itens={[
        { rotulo: 'Faturas e Cobranças', para: '/financeiro/faturas', icone: FileText },
        { rotulo: 'Conferência do faturamento', para: '/financeiro/conferencia-faturamento', icone: ClipboardCheck },
        { rotulo: 'Recebimento de faturas', para: '/financeiro/recebimento-faturas', icone: FileInput },
        { rotulo: 'Resumo financeiro', para: '/financeiro', icone: PieChart },
      ]} />
    </div>
  );
};

export default TelaFaturamento;
